import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Users } from "lucide-react";
import { useGetMembersQuery } from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import { ViewMemberDialog } from "@/features/members-groups/presentation/components/ViewMemberDialog";
import type { Member } from "@/features/members-groups/domain/entities/Member";
import type { OldRole as Role } from "../../domain";

interface ViewRoleMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
  canViewMember?: boolean;
}

export const ViewRoleMembersDialog: React.FC<ViewRoleMembersDialogProps> = ({
  open,
  onOpenChange,
  role,
  canViewMember = true,
}) => {
  const { t } = useTranslation("rolesPermissions");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const { data: members = [], isLoading } = useGetMembersQuery(
    role?.id ? { role_ids: [role.id], pageSize: 100 } : undefined,
    { skip: !open || !role?.id, refetchOnMountOrArgChange: true },
  );

  const handleMemberClick = (member: Member) => {
    if (!canViewMember) return;
    setSelectedMember(member);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-h-[80vh] sm:max-w-[520px]">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle className="text-lg font-semibold">
              {role?.name ?? t("viewRoleMembersDialog.defaultRoleName")} - {t("viewRoleMembersDialog.titleSuffix")}
            </DialogTitle>
            {!isLoading && (
              <p className="text-sm text-gray-500">
                {t("viewRoleMembersDialog.members", { count: members.length })}
              </p>
            )}
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : members.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-gray-400">
                <Users className="h-8 w-8" />
                <p className="text-sm">{t("viewRoleMembersDialog.empty")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleMemberClick(member)}
                    disabled={!canViewMember}
                    className={`flex w-full items-center gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-left transition-colors ${
                      canViewMember
                        ? "cursor-pointer hover:bg-gray-100"
                        : "cursor-default"
                    }`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {member.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {member.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {member.email}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ViewMemberDialog
        open={!!selectedMember}
        onOpenChange={(val) => {
          if (!val) setSelectedMember(null);
        }}
        member={selectedMember}
      />
    </>
  );
};
